/**
 * The capability catalog — the cards on the front page.
 *
 * Static configuration, as `docs/SCREENS.md` §1 says, with one exception that matters: the proof
 * points beside it read live counts. A client-facing page whose numbers are hardcoded is a brochure,
 * and the first customer to compare it with their dashboard stops trusting both.
 *
 * `route` is null for the capabilities that have no screen of their own. It is null rather than
 * pointing somewhere plausible, so that the UI can say "configured during onboarding" instead of
 * navigating a prospect to a page that does not answer the question the card raised.
 *
 * The count is deliberately not stated here. It was "fourteen" until the security-capability layer
 * added four, and a number in a comment is the drift this repository gates against everywhere else.
 * `catalog.test.ts` asserts it; this describes it.
 */

export type Availability = "core" | "addon" | "soon";

export interface Capability {
  id: string;
  name: string;
  availability: Availability;
  description: string;
  /** In-app route this card deep-links to, or null when the capability has no screen. */
  route: string | null;
  group: string;
  /**
   * Which build phase delivers it. Present because phases 0–2 are what exists today, and a catalog
   * that lists every card as though it were live is the same brochure problem one level in.
   */
  phase: 0 | 1 | 2 | 3 | 4;
}

export const CAPABILITIES: readonly Capability[] = [
  {
    id: "identity-management",
    name: "Identity management",
    availability: "core",
    description:
      "One directory for humans and agents with the same lifecycle — join, change, leave. SSO and MFA for people; owned, tiered, expiring identities for agents.",
    route: "/identities",
    group: "Identity",
    phase: 0,
  },
  {
    id: "access-management",
    name: "Access management",
    availability: "core",
    description:
      "Requests, entitlement bundles, and just-in-time elevation for humans and agents through one policy engine. Standing privilege is the justified exception for both.",
    route: "/access",
    group: "Access",
    phase: 1,
  },
  {
    id: "agent-registry",
    name: "Agent identity registry",
    availability: "core",
    description:
      "Every agent registered with a named owner, purpose, lifecycle state, and capability tier.",
    route: "/agents",
    group: "Identity",
    phase: 0,
  },
  {
    id: "short-lived-credentials",
    name: "Short-lived agent credentials",
    availability: "core",
    description:
      "Task-scoped tokens issued just-in-time through your existing PAM or secrets vendor. No standing keys.",
    route: "/agents",
    group: "Privilege",
    phase: 1,
  },
  {
    id: "capability-tiers",
    name: "Capability tiers & independent veto",
    availability: "core",
    description:
      "Tier 1–3 by impact. Tier 3 promotions route to a security approver outside the owning team.",
    route: "/agents",
    group: "Governance",
    phase: 1,
  },
  {
    id: "decision-chain",
    name: "Signed decision chain",
    availability: "core",
    description:
      "Hash-linked record of request → policy → credential → action → expiry for every agent action.",
    route: "/agents",
    group: "Audit",
    phase: 2,
  },
  {
    id: "onboarding-gates",
    name: "Onboarding gates",
    availability: "core",
    description:
      "One intake for agents, systems, and third parties; scorecard-driven review depth; eight specialized gates.",
    route: "/onboard",
    group: "Governance",
    phase: 4,
  },
  {
    id: "access-reviews",
    name: "Access reviews (UAR)",
    availability: "core",
    description:
      "Five review scopes with reviewer assignment, exception tracking, and locked evidence at cycle close.",
    route: "/reviews",
    group: "Governance",
    phase: 4,
  },
  {
    id: "access-graph",
    name: "Access graph",
    availability: "addon",
    description:
      "Effective permissions across every connected system and data tier, sourced from Veza or equivalent.",
    route: "/graph",
    group: "Visibility",
    phase: 2,
  },
  {
    id: "fine-grained-policies",
    name: "Fine-grained policies",
    availability: "addon",
    description:
      "Attribute-based rules evaluated per request — subject, action, data, context, and declared purpose.",
    route: "/policies",
    group: "Access",
    phase: 3,
  },
  {
    id: "runtime-monitoring",
    name: "Runtime agent monitoring",
    availability: "addon",
    description:
      "Fast check on every tool call; anomalies escalate to deeper review. Maps to the OWASP agentic risk set.",
    route: "/alerts",
    group: "Runtime",
    phase: 3,
  },
  {
    id: "data-classification",
    name: "Data classification & encryption",
    availability: "addon",
    description:
      "Sensitivity tags at creation that drive field-level encryption and which policies apply downstream.",
    route: null,
    group: "Data",
    phase: 3,
  },
  {
    id: "evidence-export",
    name: "Compliance evidence export",
    availability: "addon",
    description:
      "Signed evidence bundles mapped to SOC 2, ISO 27001/42001, GDPR, and CPRA control requirements.",
    route: null,
    group: "Compliance",
    phase: 4,
  },
  {
    id: "threat-model",
    name: "Agentic threat model",
    availability: "core",
    description:
      "The seven OWASP agentic risk classes plus identity blending and indirect injection, each assessed against the controls that answer it — and each control's state is a live query, never a tick.",
    route: "/threats",
    group: "Governance",
    phase: 2,
  },
  {
    id: "tool-governance",
    name: "Tool registry & combination review",
    availability: "core",
    description:
      "Tools scoped per task rather than per agent, pinned and reviewed at source, with the combinations that are safe apart and dangerous together flagged before the grant.",
    route: "/tools",
    group: "Privilege",
    phase: 2,
  },
  {
    id: "adversarial-assurance",
    name: "Adversarial assurance",
    availability: "core",
    description:
      "Red-team campaigns recorded against your own agents, their findings tracked to closure, and a tier-3 promotion gate that refuses on stale, dependent or unremediated evidence.",
    route: "/assurance",
    group: "Governance",
    phase: 2,
  },
  {
    id: "framework-mapping",
    name: "Framework evidence",
    availability: "addon",
    description:
      "Controls mapped to SOC 2, ISO 27001/42001, NIST CSF 2.0, GDPR, CPRA and the EU AI Act — reported as measured, failing and unmeasurable, never as a readiness percentage.",
    route: "/assurance",
    group: "Compliance",
    phase: 2,
  },
  {
    id: "agent-to-agent-trust",
    name: "Agent-to-agent trust",
    availability: "soon",
    description:
      "Cryptographic provenance for decisions made between your agents and other organizations' agents.",
    route: null,
    group: "Future",
    phase: 4,
  },
] as const;

/** Which phases this deployment has actually built. Everything above it is honest about not existing. */
export const DELIVERED_THROUGH_PHASE = 2;

export function isLive(c: Capability): boolean {
  return c.phase <= DELIVERED_THROUGH_PHASE;
}

/** The navigation order, from `docs/SCREENS.md`. The alerts entry carries a count badge. */
export const NAV: readonly { label: string; route: string; short: string; phase: number }[] = [
  { label: "Capabilities", route: "/", short: "Start", phase: 0 },
  { label: "Dashboard", route: "/dashboard", short: "Home", phase: 2 },
  { label: "Identity management", route: "/identities", short: "Identity", phase: 0 },
  { label: "SSO", route: "/sso", short: "SSO", phase: 0 },
  { label: "Access management", route: "/access", short: "Access", phase: 1 },
  { label: "Agents", route: "/agents", short: "Agents", phase: 0 },
  { label: "Access graph", route: "/graph", short: "Graph", phase: 2 },
  { label: "Threat model", route: "/threats", short: "Threats", phase: 2 },
  { label: "Tools", route: "/tools", short: "Tools", phase: 2 },
  { label: "Assurance", route: "/assurance", short: "Assurance", phase: 2 },
  { label: "Access reviews", route: "/reviews", short: "Reviews", phase: 4 },
  { label: "Onboarding", route: "/onboard", short: "Onboard", phase: 4 },
  { label: "Policies", route: "/policies", short: "Policies", phase: 3 },
  { label: "Alerts", route: "/alerts", short: "Alerts", phase: 3 },
] as const;

/** The five entries the mobile bottom bar carries. */
export const MOBILE_TABS = ["/", "/dashboard", "/identities", "/access", "/agents"] as const;
